import { Descriptions, Spin } from 'antd';
import classnames from 'classnames';
import React, { useEffect } from 'react';

import { Can } from '@client/components/Can';
import { EAbilityActions } from '@client/hooks/useAbilities';
import { AccessDeniedPage } from '@client/routes/lazy-routes';
import { useDirty } from '@client/store/dirty.store';
import { useLoading } from '@client/store/loading.store';
import { useLocale } from '@client/store/locale.store';
import { isRtlLocale } from '@common/types';

import styles from './layout.module.less';

type TPageProps = {
  title: React.ReactNode | string;
  description?: React.ReactNode | string;
  action?: string;
  subject?: string;
  children?: React.ReactNode;
  extra?: React.ReactNode[];
};

export const PageLayout = (props: TPageProps): React.JSX.Element => {
  const {
    action = EAbilityActions.READ,
    subject = 'page',
    title,
    description,
    extra = [],
  } = props;

  const { setDirty } = useDirty();
  const { loading } = useLoading();
  const { locale } = useLocale();

  useEffect(() => () => setDirty(false), [setDirty]);

  return (
    <div className={styles.pageLayout}>
      <Can I={action} do={subject}>
        <div>
          <Descriptions
            title={title}
            className={classnames({ [styles.description]: description })}
          >
            {description ? (
              <Descriptions.Item>{description}</Descriptions.Item>
            ) : null}
          </Descriptions>
          <div
            className={classnames(styles.extra, {
              [styles.rtl]: isRtlLocale(locale),
            })}
          >
            {extra.map((item, idx) => (
              <div key={idx}>{item}</div>
            ))}
          </div>
        </div>
        <Spin spinning={loading}>
          <div>{props.children}</div>
        </Spin>
      </Can>
      <Can not I={action} do={subject}>
        <AccessDeniedPage />
      </Can>
    </div>
  );
};
